/**
 * Quotes List Page.
 *
 * Displays all quotes with status badges, quick create, and navigation
 * to the quote detail/builder page. Embedded in the Sales Hub.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FileText, Send, Check, X, RefreshCw } from "lucide-react";
import { useQuotes, useCreateQuote } from "@/features/crm/api";
import { useCustomers } from "@/features/sales/api";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import ComboSelect from "@/components/ui/ComboSelect";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

const statusVariant: Record<
  string,
  "default" | "success" | "warning" | "danger" | "info"
> = {
  draft: "default",
  sent: "info",
  viewed: "info",
  accepted: "success",
  declined: "danger",
  expired: "warning",
  revised: "default",
};

const statusIcon: Record<string, React.ElementType> = {
  draft: FileText,
  sent: Send,
  accepted: Check,
  declined: X,
  revised: RefreshCw,
};

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

function QuotesListPage() {
  const navigate = useNavigate();
  const { data: quotesData, isLoading } = useQuotes();
  const { data: customersData } = useCustomers();
  const createMutation = useCreateQuote();

  const [showCreate, setShowCreate] = useState(false);
  const [customerId, setCustomerId] = useState("");

  const quotes = quotesData?.items || [];
  const customers = customersData?.items || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = (await createMutation.mutateAsync({
      customer_id: customerId,
    })) as Record<string, unknown>;
    setShowCreate(false);
    setCustomerId("");
    navigate(`/quotes/${result.id}`);
    toast.success("Quote created");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Quotes
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Create and manage customer quotes and proposals.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? (
            "Cancel"
          ) : (
            <>
              <Plus className="h-4 w-4" /> New Quote
            </>
          )}
        </Button>
      </div>

      {showCreate && (
        <Card>
          <form
            onSubmit={handleCreate}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <ComboSelect
              label="Customer"
              options={customers.map((c) => ({
                value: c.id,
                label: c.name,
                sublabel: c.company_name || undefined,
              }))}
              value={customerId}
              onChange={setCustomerId}
              placeholder="Select customer..."
              required
            />
            <div className="flex items-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Create Quote
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : quotes.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {quotes.map((q) => {
            const StatusIcon = statusIcon[q.status] || FileText;
            return (
              <Link key={q.id} to={`/quotes/${q.id}`}>
                <Card padding="sm" className="hover-lift">
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                        q.status === "accepted"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                          : q.status === "declined"
                            ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                            : "bg-brand-200 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300"
                      }`}
                    >
                      <StatusIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                          {q.quote_number}
                        </span>
                        <Badge variant={statusVariant[q.status] || "default"}>
                          {q.status}
                        </Badge>
                        <span className="text-xs text-[var(--text-tertiary)]">
                          v{q.version}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                        {new Date(q.created_at).toLocaleDateString()}
                        {q.valid_until && (
                          <span className="ml-2">
                            Valid until{" "}
                            {new Date(q.valid_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-brand-600">
                        {fmt(q.total)}
                      </p>
                      {q.trade_in_credit > 0 && (
                        <p className="text-[10px] text-[var(--text-success)]">
                          Trade-in: ({fmt(q.trade_in_credit)})
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <FileText />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No quotes yet
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create a quote to start the negotiation process with a customer.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default QuotesListPage;
